import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../ManufacturingRecordSlice";
import { fetchManufacturingRecord } from "../ManufacturingRecordThunk";

import { useDefaultDateRange } from "../../../../hooks/useDefaultDateRange";
import "./style.css";

import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import RecordDateFilter from "../../../../components/filters/RecordDateFilter";

import {
  fetchEmployees,
  fetchSuppliers,
  fetchItems,
  fetchGroups,
  fetchCategories,
  fetchUsers,
  fetchTagGroups,
  fetchLocations,
} from "../../../../store/commonThunks/lookups";
import CommonDateRangeFilter from "../../../../components/common/filters/CommonDateRangeFilter";

const ManufacturingRecordFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.manufacturingRecord);
  const lookups = useSelector((state) => state.lookups);

  useDefaultDateRange(setFilters, filters, "thisMonth"); // 👈 defaults to today //default date using custom hook

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchGroups());
    dispatch(fetchSuppliers());
    dispatch(fetchItems());
    dispatch(fetchCategories());
    dispatch(fetchUsers());
    dispatch(fetchTagGroups());
    dispatch(fetchLocations());
  }, [dispatch]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    const payload = {
      selectedFilterType: filters.filterType,
      selectedRecordType: filters.recordType,
      filters: {
        ...filters,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      },
    };

    dispatch(fetchManufacturingRecord(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  // 🔹 dynamic extra filter fields
  const renderExtraField = () => {
    switch (filters.filterType) {
      case "By User":
        return (
          <Col xs={24} sm={12} md={8} xl={5}>
            <label>Search User</label>
            <SearchListAutoComplete
              entityType="user"
              optionsList={lookups.users.map((u) => ({
                sale_id: u.id,
                display_text: u.full_name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="User"
              showAddNew={false}
            />
          </Col>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="product-replace-filters mb-4 shadow-md rounded-xl">
      <Row
        gutter={[16, 16]}
        style={{ alignItems: "center" }}
        align="middle"
        wrap
      >
        <Col xs={24} sm={12} md={4} lg={6}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By User", display_text: "By User" },
            ]}
            value={
              filters.filterType
                ? { value: filters.filterType, label: filters.filterType }
                : undefined
            }
            onSelect={(obj) => {
              if(!obj) {
                handleFilterChange("filterType", null);
                return;
              } 
              handleFilterChange("filterType", obj?.sale_id);
            }}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {renderExtraField()}

        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
          <label>Record Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "with item", display_text: "With Item" },
              { sale_id: "without item", display_text: "Without Item" },
            ]}
            
            value={
              filters.recordType
                ? { value: filters.recordType, label: filters.recordType === "with item"? "With Item" : "Without Item"}
                : undefined
            }
            onSelect={(obj) => {
              if(!obj) {
                handleFilterChange("recordType", null);
                return;
              } 
              handleFilterChange("recordType", obj?.sale_id);
            }}
            placeholder="Record Type"
            showAddNew={false}
          />
        </Col>

        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />

        <Col xs={24} sm={12} md={9} xl={3} style={{ paddingTop: "1.4rem" }}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            block
            data-testid="manufacture-record-button"
          >
            Report
          </Button>
        </Col>
      </Row>
      <style>{`
  @media (min-width: 768px) and (max-width: 1024px) {
    .product-replace-filters .ant-row .ant-col {
      flex: 0 0 50% !important;
      max-width: 50% !important;
    }
  }

  @media (min-width: 768px) {
    :where(.css-dev-only-do-not-override-1j5i8gz).ant-col-md-4 {
      max-width: none !important;
      flex: none !important;
    }
  }
`}</style>
    </Card>
  );
};

export default ManufacturingRecordFilters;
